import type { ResearchBulkAction, ResearchBulkResponse, ResearchRecord } from "@pokifolio/contracts";
import { useId, useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "../../components/confirm-dialog";
import { Textarea } from "../../components/ui/input";
import { describeError } from "../../lib/errors";
import { useResearchBulk } from "./research-queries";

/** What the person asked for: close or delete these records. */
export interface ManageRequest {
  readonly action: ResearchBulkAction;
  readonly records: readonly ResearchRecord[];
}

const name = (r: ResearchRecord) => `${r.card.name} ${r.card.number}`;

/** "3 research records closed." / "Venusaur EX XY28 deleted." */
function doneMessage(action: ResearchBulkAction, done: readonly ResearchRecord[]): string {
  const verb = action === "close" ? "closed" : "deleted";
  const [only] = done;
  return done.length === 1 && only !== undefined
    ? `${name(only)} ${verb}.`
    : `${String(done.length)} research records ${verb}.`;
}

/** Shows what was done and why anything was not. */
function report(request: ManageRequest, response: ResearchBulkResponse): void {
  const byId = new Map(request.records.map((r) => [r.id, r]));
  const done = response.results.flatMap((r) => (r.ok ? (byId.get(r.id) ?? []) : []));
  const refused = response.results.flatMap((r) => (r.ok ? [] : [r]));
  if (done.length > 0) toast.success(doneMessage(request.action, done));
  for (const r of refused) {
    const record = byId.get(r.id);
    toast.error(record === undefined ? r.message : `${name(record)}: ${r.message}`);
  }
}

interface ManageResearchDialogProps {
  readonly request: ManageRequest | null;
  readonly onDone: () => void;
}

/** Asks once more before closing or deleting one or more records, then does it and reports in a toast. */
export function ManageResearchDialog({ request, onDone }: ManageResearchDialogProps) {
  const bulk = useResearchBulk();
  const [reason, setReason] = useState("");
  const reasonId = useId();

  if (request === null) return null;
  const { action, records } = request;
  const count = records.length;
  const [only] = records;
  const what = count === 1 && only !== undefined ? name(only) : `${String(count)} research records`;

  const confirm = () =>
    bulk.mutate(
      {
        action,
        items: records.map((r) => ({ id: r.id, version: r.version })),
        ...(action === "close" ? { reason } : {}),
      },
      {
        onSuccess: (response) => {
          report(request, response);
          setReason("");
          onDone();
        },
        onError: (error) => toast.error(describeError(error).message),
      },
    );

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open && !bulk.isPending) onDone();
      }}
      title={action === "close" ? `Close ${what}?` : `Delete ${what}?`}
      description={
        action === "close"
          ? "Closed research stays in the list under “Closed”, and the listing can be saved again."
          : "It disappears from every list. Its history is kept for the record, and the listing can be saved again."
      }
      confirmText={action === "close" ? "Close" : "Delete"}
      destructive={action === "delete"}
      busy={bulk.isPending}
      onConfirm={confirm}
    >
      {action === "close" && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={reasonId} className="text-sm font-medium text-foreground">
            Why close it? (optional)
          </label>
          <Textarea
            id={reasonId}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={2000}
          />
        </div>
      )}
    </ConfirmDialog>
  );
}
